<!-- Displays and edits the list of BusinessEntity references on a ProcessElement. -->
<!-- In edit mode, allows adding/removing entity refs; saves immediately on each change. -->
<template>
    <div class="business-entity-refs">
        <DataTable
            v-if="localRefs.length > 0"
            :value="enrichedRefs"
            tableStyle="width: 100%"
        >
            <Column>
                <template #body="{ data }">
                    <span class="relationship-sentence">
                        <span class="relationship-type">{{ data.label }}</span>
                        <RouterLink
                            :id="`entity-ref-link-${data.index}`"
                            class="entity-reference"
                            :to="{
                                name: 'businessEntityPage',
                                params: {
                                    orgName: $route.params.orgName,
                                    stekName: $route.params.stekName,
                                    businessEntityId: data.entity_id,
                                },
                            }"
                            >{{ data.entityName }}</RouterLink
                        >
                    </span>
                </template>
            </Column>
            <Column
                v-if="isEditing"
                :exportable="false"
                style="width: 80px"
                bodyStyle="text-align: right"
            >
                <template #body="{ data }">
                    <Button
                        v-tooltip.top="'Remove linked domain object'"
                        icon="pi pi-times"
                        text
                        rounded
                        severity="danger"
                        :loading="isSaving"
                        @click="removeRef(data.index)"
                    />
                </template>
            </Column>
        </DataTable>

        <div v-if="isEditing" class="add-entity-ref-form">
            <div class="form-row">
                <div class="form-field">
                    <label for="entityRefLabel">Relationship</label>
                    <Dropdown
                        id="entityRefLabel"
                        v-model="newLabel"
                        :options="labelOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select relationship (or type a custom one)"
                        class="dropdown-full-width"
                        editable
                    />
                </div>
                <div class="form-field">
                    <label for="entityRefTarget">Domain Object</label>
                    <Dropdown
                        id="entityRefTarget"
                        v-model="selectedEntityId"
                        :options="entities"
                        optionLabel="name"
                        optionValue="identity"
                        placeholder="Select entity"
                        :loading="isLoadingEntities"
                        class="dropdown-full-width"
                    />
                </div>
                <Button
                    icon="pi pi-plus"
                    label="Add"
                    :disabled="!canAdd"
                    :loading="isSaving"
                    @click="addRef"
                />
            </div>
        </div>
    </div>

    <!-- Hover popups for each entity ref link (keyed by index for uniqueness) -->
    <template v-for="(ref, idx) in localRefs" :key="`entity-ref-popup-${idx}`">
        <Popup v-if="!isEditing" :anchor="`a#entity-ref-link-${idx}`">
            <GenericCard
                :entityId="ref.entity_id"
                :entityType="EntityType.BUSINESS_ENTITY"
                :showPath="true"
            />
        </Popup>
    </template>
</template>

<script setup lang="ts">
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dropdown from "primevue/dropdown";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useEditManager } from "@/composables/edit-manager";
import { api, type types } from "@/lib/openapi";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import { useProcessElementsStore } from "@/stores/process-elements-store";

const props = defineProps<{
    elementId: string;
    entityRefs: types.EntityRef[];
}>();

const $route = useRoute();
const toast = useToast();
const { isEditing } = useEditManager();
const { entities, isLoading: isLoadingEntities } = useBusinessEntitiesManager();
const processElementsStore = useProcessElementsStore();

const localRefs = ref<types.EntityRef[]>([...props.entityRefs]);
const isSaving = ref(false);
const selectedEntityId = ref<string | null>(null);
const newLabel = ref("");

const labelOptions = [
    { label: "reads", value: "reads" },
    { label: "creates", value: "creates" },
    { label: "updates", value: "updates" },
    { label: "deletes", value: "deletes" },
    { label: "validates", value: "validates" },
    { label: "uses", value: "uses" },
    { label: "triggers", value: "triggers" },
    { label: "depends on", value: "depends on" },
];

// Keep localRefs in sync when the parent reloads the element.
watch(
    () => props.entityRefs,
    (incoming) => {
        localRefs.value = [...incoming];
    }
);

const enrichedRefs = computed(() =>
    localRefs.value.map((ref, index) => ({
        ...ref,
        index,
        entityName:
            entities.value.find((e) => e.identity === ref.entity_id)?.name ??
            ref.entity_id,
    }))
);

const canAdd = computed(
    () => Boolean(selectedEntityId.value) && Boolean(newLabel.value?.trim())
);

const saveRefs = async (refs: types.EntityRef[]) => {
    isSaving.value = true;
    try {
        const { response } =
            await api.startProcessProcessElementMgtUpdateProcessElementEntityRefsStartPost(
                {
                    body: {
                        process_element_id: props.elementId,
                        entity_refs: refs,
                    },
                }
            );
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        localRefs.value = refs;
        // Refresh the process elements store so BPMN overlays update immediately
        await processElementsStore.refresh();
    } catch {
        toast.add({
            severity: "error",
            summary: "Could not update business entity references",
        });
    } finally {
        isSaving.value = false;
    }
};

const addRef = async () => {
    if (!canAdd.value || !selectedEntityId.value) return;

    const updated: types.EntityRef[] = [
        ...localRefs.value,
        {
            entity_id: selectedEntityId.value,
            label: newLabel.value.trim(),
        },
    ];
    await saveRefs(updated);
    selectedEntityId.value = null;
    newLabel.value = "";
};

/** Remove a ref by its position in the list (allows duplicate entity_ids). */
const removeRef = async (index: number) => {
    const updated = localRefs.value.filter((_, i) => i !== index);
    await saveRefs(updated);
};
</script>

<style scoped lang="scss">
.relationship-sentence {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    flex-wrap: wrap;
    white-space: pre-wrap;
}

.relationship-type {
    font-style: italic;
}

.entity-reference {
    color: var(--stekz-primary-color) !important;
    font-weight: bold;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.add-entity-ref-form {
    margin-top: 1rem;
}

.form-row {
    display: flex;
    gap: 0.5rem;
    align-items: end;
    flex-wrap: wrap;
}

.form-field {
    display: flex;
    flex-direction: column;
    min-width: 180px;
    flex: 1;
}

.form-field label {
    margin-bottom: 0.25rem;
    font-weight: 500;
}

.dropdown-full-width {
    width: 100%;
}
</style>
